import { CosmosWallet, WalletErrors, EthereumWallet, WalletBackend } from '@onomy/wallet';
import * as ethers from 'ethers';
import { VoidSigner } from 'ethers';

class KeplrCosmosWallet extends CosmosWallet {
  constructor() {
    super(...arguments);
    this.keplrConnected = false;
  }
  getId() {
    return "keplr";
  }
  getDisplayName() {
    return "Keplr";
  }
  getInstallUrl() {
    return "https://www.keplr.app/";
  }
  isAvailable() {
    try {
      if (!this.getKeplr())
        throw new WalletErrors.NotSupported("Keplr not present");
      return true;
    } catch (e) {
      if (e instanceof WalletErrors.NotSupported)
        return false;
      throw e;
    }
  }
  isConnected() {
    return this.keplrConnected;
  }
  async disconnect() {
    this.keplrConnected = false;
  }
  async getAccounts() {
    try {
      if (!this.isConnected())
        return [];
      const keplr = this.getKeplr();
      const signer = await keplr.getOfflineSigner(this.chainId);
      const accounts = await signer.getAccounts();
      return accounts;
    } catch (e) {
      return [];
    }
  }
  async signDirect(signerAddress, signDoc) {
    const keplr = this.getKeplr();
    const signer = await keplr.getOfflineSigner(this.chainId);
    const response = await signer.signDirect(signerAddress, signDoc);
    return response;
  }
  async connect() {
    try {
      const keplr = this.getKeplr();
      await keplr.enable(this.chainId);
      await this.connectActual();
    } catch (e) {
      if (e == null ? void 0 : e.message.indexOf("no chain info")) {
        await new Promise((resolve, reject) => {
          setTimeout(async () => {
            this.connectActual().then(resolve, reject);
          });
        });
      } else {
        console.error("connect error", e);
        throw e;
      }
    }
  }
  async connectActual() {
    if (this.keplrConnected)
      return;
    try {
      const keplr = this.getKeplr();
      if (this.chainInfo)
        await keplr.experimentalSuggestChain(this.chainInfo);
      await keplr.enable(this.chainId);
      this.keplrSigner = await keplr.getOfflineSignerAuto(this.chainId);
      this.keplrConnected = true;
      window.addEventListener("keplr_keystorechange", () => {
        window.location.reload();
      });
    } catch (e) {
      console.error("keplr error", e);
      throw e;
    }
  }
  getSigner() {
    var _a;
    if (!this.isConnected())
      return null;
    return (_a = this.keplrSigner) != null ? _a : this;
  }
  getKeplr() {
    const keplrWindow = window;
    const { keplr } = keplrWindow;
    if (!keplr || keplr.version === "0.0.0")
      throw new WalletErrors.NotSupported("Keplr not present");
    return keplr;
  }
}

class CosmostationKeplrCosmosWallet extends KeplrCosmosWallet {
  getId() {
    return "cosmostation-cosmos";
  }
  getDisplayName() {
    return "Cosmostation";
  }
  getInstallUrl() {
    return "https://chrome.google.com/webstore/detail/cosmostation-wallet/fpkhgmpbidmiogeglndfbkegfdlnajnf?utm_source=chrome-ntp-icon";
  }
  getKeplr() {
    var _a, _b;
    const keplr = (_b = (_a = window.cosmostation) == null ? void 0 : _a.providers) == null ? void 0 : _b.keplr;
    if (!keplr)
      throw new WalletErrors.NotSupported("Cosmostation not present");
    return keplr;
  }
}

class WrappedEthereumWallet extends EthereumWallet {
  constructor(ethWsUrl, provider, signer, logout) {
    super(ethWsUrl);
    this.provider = provider;
    this.signer = signer;
    this.logoutFn = logout;
    if (provider) {
      provider.on("error", (e) => {
        console.error("eth error", e);
      });
    }
  }
  async disconnect() {
    window.localStorage.removeItem("connectorId");
    if (this.logoutFn)
      this.logoutFn();
  }
  async getSigner() {
    if (!this.signer)
      return new VoidSigner("0x0000000000000010000000000000000000000000", await this.getProvider());
    return this.signer;
  }
  async getProvider() {
    if (!this.provider)
      return super.getProvider();
    return this.provider;
  }
}

class MetamaskEthereumWallet extends WrappedEthereumWallet {
  constructor(ethWsUrl) {
    super(ethWsUrl);
    var _a;
    this.provider = window.ethereum ? new ethers.providers.Web3Provider(window.ethereum) : void 0;
    this.signer = (_a = this.provider) == null ? void 0 : _a.getSigner();
  }
  async connect() {
    var _a;
    await ((_a = this.provider) == null ? void 0 : _a.send("eth_requestAccounts", []));
    function reloadWindow() {
      window.location.reload();
    }
    window.ethereum.on("accountsChanged", reloadWindow);
    window.ethereum.on("networkChanged", reloadWindow);
  }
  getId() {
    return "metamask";
  }
  isAvailable() {
    return "ethereum" in window;
  }
  getDisplayName() {
    return "Metamask";
  }
  getInstallUrl() {
    return "https://metamask.io/download.html";
  }
}

class LeapKeplrCosmosWallet extends KeplrCosmosWallet {
  getId() {
    return "leap-cosmos";
  }
  getDisplayName() {
    return "Leap Wallet";
  }
  getInstallUrl() {
    return "https://www.leapwallet.io/#inpage-download";
  }
  getKeplr() {
    const keplr = window.leap;
    if (!keplr)
      throw new WalletErrors.NotSupported("Leap Wallet not present");
    return keplr;
  }
}

class WebWalletBackend extends WalletBackend {
  constructor(options = {}) {
    super();
    this.ethereumProvider = options.ethereumProvider;
    this.ethereumSigner = options.ethereumSigner;
    this.ethereumLogout = options.ethereumLogout;
  }
  async unlock(_password) {
    return true;
  }
  getCosmosWallets(chainId, chainInfo) {
    const cosmos = new CosmostationKeplrCosmosWallet(chainId, chainInfo);
    const leap = new LeapKeplrCosmosWallet(chainId, chainInfo);
    const keplr = new KeplrCosmosWallet(chainId, chainInfo);
    return [cosmos, leap, keplr];
  }
  cosmos(chainId, chainInfo) {
    return new KeplrCosmosWallet(chainId, chainInfo);
  }
  getEthereumWallets(ethWsUrl) {
    const metamask = new MetamaskEthereumWallet(ethWsUrl);
    return [metamask];
  }
  ethereum(ethWsUrl) {
    return new WrappedEthereumWallet(ethWsUrl, this.ethereumProvider, this.ethereumSigner, this.ethereumLogout);
  }
}

export { WebWalletBackend };
